/**
 * Toaster — Sonner viewport, themed for the warm palette.
 * Mount once near the application root (App.tsx in a later task).
 *
 * Live-VPS UI review (2026-05-08, defect #1): with Sonner's `richColors` the
 * library injects its own CSS variables (`--success-bg`, `--error-bg`, …)
 * which evaluate to default red/green/blue and clash with the warm theme.
 * The fix is to drop `richColors` and instead pin every variant to our
 * tokenised classes. Sonner applies the variant-specific class on the
 * toast `<li>` (default for the base toast and on top of `success` /
 * `error` / `warning` / `info` toast types) so the rules below stick.
 *
 * Token mapping (warm theme — see styles/tokens.css):
 *   - success → ok     (#3F8D5C / wash #E6F0EA)
 *   - error   → danger (#A62A1C / wash #F8DBD6 — deep oxblood; since the
 *                       impeccable P1 audit `--danger` is split OUT of
 *                       `--accent` so destructive/error surfaces read
 *                       distinct from the terracotta brand accent)
 *   - warning → warn   (#C88A2A / wash #F6EEDC)
 *   - info    → info   (#3A6FB0 / wash #E8EEF6)
 *
 * The base shell (border + bg + ink) stays warm too so neutral toasts
 * still match the rest of the chrome.
 */
import { Toaster as SonnerToaster } from 'sonner';

export function Toaster() {
  return (
    <SonnerToaster
      position="bottom-right"
      closeButton
      // No richColors — we own the palette. See file header for why.
      toastOptions={{
        classNames: {
          // Neutral / default surface — warm card with ink text.
          toast: 'border bg-card text-ink shadow-2 rounded-md font-sans border-line',
          title: 'text-ink',
          description: 'text-muted',
          actionButton: 'bg-primary text-primary-foreground rounded-sm',
          cancelButton: 'bg-surface-2 text-ink rounded-sm',
          // Variant-specific surfaces — Sonner stacks these classes on
          // the toast root for each type.
          success: 'bg-ok-wash text-ink border-ok',
          error: 'bg-danger-wash text-danger border-danger',
          warning: 'bg-warn-wash text-ink border-warn',
          info: 'bg-info-wash text-ink border-info',
        },
      }}
    />
  );
}
